import type { Immutable } from "immer";
import React, { useCallback, useEffect, useState } from "react";

import { ICountdownTimer } from "../../models/countdown-timer";
import { CountdownTimer as Component } from "./CountdownTimerDisplay";

type IProps = Immutable<{
    timerData: ICountdownTimer[];
    timersExpiredPromo: React.ReactNode;
    onTimerReady?: () => void;
}>;

const hasExpiredPromo = (timersExpiredPromo: React.ReactNode): boolean => {
    return (
        timersExpiredPromo !== "" &&
        timersExpiredPromo !== null &&
        timersExpiredPromo !== undefined &&
        timersExpiredPromo !== false
    );
};

export const CountdownTimer = (props: IProps): React.ReactNode => {
    const [timers, setTimers] = useState<readonly ICountdownTimer[]>([]);
    const [currentTimer, setCurrentTimer] = useState<ICountdownTimer | null>(
        null,
    );
    const hasTimersExpiredPromo = hasExpiredPromo(props.timersExpiredPromo);

    // Required: Used in useEffect dependency array to prevent infinite loop
    const getCurrentTimer = useCallback(
        (timersList: readonly ICountdownTimer[]) => {
            if (timersList.length === 0) {
                setCurrentTimer(null);
                if (hasTimersExpiredPromo) {
                    props.onTimerReady?.();
                }
                return;
            }

            const now = new Date().getTime();
            const testStart = Date.parse(timersList[0].start_date);
            const testEnd = Date.parse(timersList[0].end_date);
            if (testStart <= now && testEnd >= now) {
                setCurrentTimer(timersList[0]);
                props.onTimerReady?.();
            } else {
                const remainingTimers = timersList.slice(1);
                setTimers(remainingTimers);
                getCurrentTimer(remainingTimers);
            }
        },
        [hasTimersExpiredPromo, props.onTimerReady],
    );

    useEffect(() => {
        setTimers(props.timerData);
        getCurrentTimer(props.timerData);
    }, [props.timerData, getCurrentTimer]);

    // Required: Passed to child component that uses it in useEffect dependency array
    const handleTimerExpired = useCallback(() => {
        getCurrentTimer(timers);
    }, [timers, getCurrentTimer]);

    const renderCountdownTimer = (): React.JSX.Element | null => {
        if (!currentTimer) {
            if (!hasTimersExpiredPromo) {
                return null;
            }

            if (typeof props.timersExpiredPromo !== "string") {
                return <div>{props.timersExpiredPromo}</div>;
            }

            return (
                <div
                    dangerouslySetInnerHTML={{
                        __html: props.timersExpiredPromo,
                    }}
                ></div>
            );
        }

        return (
            <Component
                currentTimer={currentTimer}
                onTimerExpired={handleTimerExpired}
            />
        );
    };

    return <div>{renderCountdownTimer()}</div>;
};
